import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import './ModifyStorageModal.css';

import { PropTypesCustom } from '../../../libs/helper';
import ModalBase from '../ModalBase';
import SimpleCheckbox, { CheckboxTypes } from '../../SimpleComponents/SimpleCheckbox';
import SimpleInput from '../../SimpleComponents/SimpleInput';
import SimpleTextarea from '../../SimpleComponents/SimpleTextarea';
import SimpleButton, { ButtonsWrapper, ButtonTypes } from '../../SimpleComponents/SimpleButton';

const renameModalStyles = {
	content: {
		top: '250px',
		width: '350px',
	},
};

const max_storage_name_length = 100;
const max_storage_shortname_length = 6;
const max_storage_note_length = 1000;

/**
 * Modify storage parameters modal
 * @component
 */
const ModifyStorageModal = ({ isOpen, title, storage, isNew, onResult }) => {
	const [nameValue, setNameValue] = useState('');
	const [shortnameValue, setShortnameValue] = useState('');
	const [noteValue, setNoteValue] = useState('');
	const [hiddenValue, setHiddenValue] = useState(0);
	const [publicValue, setPublicValue] = useState(0);

	useEffect(() => {
		console.log('Initialization: ModifyStorageModal');
	}, []);

	useEffect(() => {
		setNameValue(storage ? storage.name : '');
		setShortnameValue(storage ? storage.shortname : '');
		setNoteValue(storage ? storage.note : '');
		setHiddenValue(storage ? storage.hidden : 0);
		setPublicValue(storage ? storage.public : 0);
	}, [storage]);

	return (
		<ModalBase className="modal-rename" isOpen={isOpen} style={renameModalStyles} onRequestClose={handleCancel}>
			<div className="modal-header">
				<div className="modal-title">{title}</div>
				<div className="modal-close-x" onClick={handleCancel} />
			</div>
			<div className="modal-content">
				<SimpleInput
					autoFocus={true}
					limit={max_storage_name_length}
					placeholder="Name"
					title="Collection name"
					value={nameValue}
					onChange={handleChangeNameValue}
				/>
				<SimpleInput
					limit={max_storage_shortname_length}
					placeholder="Short Name"
					title="Short name for collection"
					value={shortnameValue}
					onChange={handleChangeShortnameValue}
				/>
				<SimpleTextarea
					className="note"
					limit={max_storage_note_length}
					placeholder="Any notes if required…"
					title="Notes"
					value={noteValue}
					onChange={handleChangeNote}
				/>
				{!isNew ? (
					<div>
						<SimpleCheckbox
							className="hidden"
							label="Hidden"
							state={hiddenValue}
							title="The storage will be hidden from summary and grayed out in the list"
							type={CheckboxTypes.TICK}
							onClick={handleChangeHiddenState}
						/>
						<SimpleCheckbox
							className="public"
							label="Public"
							state={publicValue}
							title="This storage will be publicly visible as one of your collections"
							type={CheckboxTypes.TICK}
							onClick={handleChangePublicState}
						/>
					</div>
				) : null}
			</div>
			<div className="modal-control">
				<ButtonsWrapper>
					<SimpleButton title="Save storage" type={ButtonTypes.DEFAULT} value="Save" onClick={handleSave} />
					<SimpleButton title="Close" type={ButtonTypes.GREY} value="Close" onClick={handleCancel} />
				</ButtonsWrapper>
			</div>
		</ModalBase>
	);

	// Reset input to defaults
	function reset() {
		setNameValue('');
		setShortnameValue('');
		setNoteValue('');
		setHiddenValue(0);
		setPublicValue(0);
	}

	// Cancel changes
	function handleCancel() {
		onResult({ status: false });
		reset();
	}

	// Apply changes and clear form data
	function handleSave() {
		onResult({
			status: true,
			data: {
				name: nameValue,
				shortname: shortnameValue,
				note: noteValue,
				hidden: hiddenValue,
				public: publicValue,
			},
		});
		reset();
	}

	function handleChangeNameValue(event) {
		let shortened = event.target.value.slice(0, max_storage_name_length);
		setNameValue(shortened);
	}

	function handleChangeShortnameValue(event) {
		let shortened = event.target.value.slice(0, max_storage_shortname_length);
		setShortnameValue(shortened);
	}

	function handleChangeNote(event) {
		let shortened = event.target.value.slice(0, max_storage_note_length);
		setNoteValue(shortened);
	}

	function handleChangeHiddenState() {
		setHiddenValue(hiddenValue ? 0 : 1);
	}

	function handleChangePublicState() {
		setPublicValue(publicValue ? 0 : 1);
	}
};

ModifyStorageModal.propTypes = {
	// Open state of modal
	isOpen: PropTypes.bool.isRequired,
	// Modal title
	title: PropTypes.string.isRequired,
	// Storage object (or null)
	storage: PropTypesCustom.object_or_null_required,
	// Flag of "add new" storage (hidden/public checkboxes not shown)
	isNew: PropTypes.bool,
	// Callback function on submit
	onResult: PropTypes.func.isRequired,
};

export default ModifyStorageModal;
